// Market-source registry (VRG-462) react-query hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { marketSourcesApi } from 'services'
import type {
  CreateMarketSourcePayload,
  IMarketSourceCompetitor,
  IMarketFetchLogEntry,
  IMarketSource,
  UpdateMarketSourcePayload,
} from 'services/marketSourcesApi'

const SOURCES_KEY = ['internal', 'market-sources']
const ITEMS_KEY = ['internal', 'market-items']
// VRG-741 — a feed added or polled changes the SEO competitors' feed counts and may feed ideas.
const SEO_KEY = ['internal', 'seo']
const IDEAS_KEY = ['internal', 'gtm', 'content-ideas']

export const useMarketSourcesQuery = () =>
  useQuery<IMarketSource[], Error>({
    queryKey: SOURCES_KEY,
    queryFn: () => marketSourcesApi.list(),
    // No retry: the expected failure is a 403 until the permission is granted, and
    // retrying three times only delays the error state by a second and a half.
    retry: false,
    refetchOnWindowFocus: false,
  })

/** One source's recent ingestion attempts — the "why is this failing" drill-in (AC7). */
export const useMarketSourceLogQuery = (id: string | undefined) =>
  useQuery<IMarketFetchLogEntry[], Error>({
    queryKey: [...SOURCES_KEY, 'log', id],
    queryFn: () => marketSourcesApi.log(id!),
    enabled: Boolean(id),
    retry: false,
  })

/**
 * Every mutation below carries `retry: false, networkMode: 'always'` — REQUIRED, not
 * stylistic. `QueryProvider`'s global `defaultOptions.mutations` sets an
 * unconditional `retry: 1` that retries a 4xx exactly like a 5xx, which left VRG-461's
 * add-by-URL modal spinning forever on a rejected URL until it was fixed the same
 * way. Root cause tracked as VRG-510; until that lands every mutation must opt out
 * individually.
 */
export const useCreateMarketSource = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateMarketSourcePayload) => marketSourcesApi.create(payload),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOURCES_KEY })
      queryClient.invalidateQueries({ queryKey: SEO_KEY })
    },
  })
}

export const useUpdateMarketSource = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateMarketSourcePayload & { id: string }) =>
      marketSourcesApi.update(id, payload),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOURCES_KEY })
      queryClient.invalidateQueries({ queryKey: SEO_KEY })
    },
  })
}

export const usePollMarketSource = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => marketSourcesApi.pollNow(id),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOURCES_KEY })
      // A poll that ingested anything changes the ITEMS list too, so that cache has
      // to go as well — otherwise a staffer polls, switches to the corpus, and sees
      // the pre-poll list.
      queryClient.invalidateQueries({ queryKey: ITEMS_KEY })
      // VRG-741: the poll also runs the corpus idea feed, so the Ideas list and run log move too.
      queryClient.invalidateQueries({ queryKey: IDEAS_KEY })
    },
  })
}

export const useDeleteMarketSource = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => marketSourcesApi.remove(id),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOURCES_KEY })
      queryClient.invalidateQueries({ queryKey: ITEMS_KEY })
    },
  })
}

/** VRG-741 — competitors a feed can belong to (for the source form's Competitor picker). */
export const useMarketSourceCompetitorsQuery = (enabled = true) =>
  useQuery<IMarketSourceCompetitor[], Error>({
    queryKey: [...SOURCES_KEY, 'competitors'],
    queryFn: () => marketSourcesApi.competitors(),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  })

/** VRG-741 — "Re-scan feeds" on one competitor: finds and ADDS its blog feeds now. */
export const useRescanCompetitorFeeds = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (domain: string) => marketSourcesApi.rescanCompetitor(domain),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOURCES_KEY })
      queryClient.invalidateQueries({ queryKey: SEO_KEY })
    },
  })
}

/** VRG-741 — "Re-scan feeds" on the Sources tab: every due competitor, in the background. */
export const useRescanAllCompetitorFeeds = () =>
  useMutation({
    mutationFn: () => marketSourcesApi.rescanAll(),
    retry: false,
    networkMode: 'always',
  })

/** VRG-741 — look for a feed or sitemap on a domain. Saves nothing. */
export const useDetectMarketSourceFeed = () =>
  useMutation({
    mutationFn: (domain: string) => marketSourcesApi.detect(domain),
    retry: false,
    networkMode: 'always',
  })
